import type { QuickNote } from "../../domain/entities.ts";
import type * as Automerge from "@automerge/automerge/slim";

export class DailyNoteConflict extends Error {
  constructor() { super("This day changed on another device"); this.name = "DailyNoteConflict"; }
}

export function validDailyDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value < "0001-01-01" || value > "9999-12-31") return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export async function dailyNoteId(rootId: string, date: string): Promise<string> {
  if (!validDailyDate(date)) throw new Error("Choose a valid date");
  const bytes = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${rootId.toLowerCase()}:daily-note:${date}`))).slice(0, 16);
  bytes[6] = (bytes[6] & 15) | 128; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function shiftDailyDate(date: string, days: number): string {
  if (!validDailyDate(date)) throw new Error("Choose a valid date");
  const next = new Date(`${date}T12:00:00Z`); next.setUTCDate(next.getUTCDate() + days);
  const result = next.toISOString().slice(0, 10);
  return validDailyDate(result) ? result : date;
}

/** All devices share an identical initial CRDT, including the empty text object. */
export function dailyNoteSeed(api: Pick<typeof Automerge, "init" | "change">, id: string, rootId: string, dailyDate: string): Automerge.Doc<QuickNote> {
  return api.change(api.init<QuickNote>({ actor: "00000000000000000000000000000032" }), { time: 0 }, draft => {
    Object.assign(draft, { id, rootId, dailyDate, body: "", pinned: false, archivedAt: null, convertedTaskId: null, linkedTaskIds: {}, deleted: false, createdAt: 0, updatedAt: 0 });
  });
}
